/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
import { isNullOrUndefined } from 'util';

import React, { useState } from 'react';

import { Switch } from '@headlessui/react';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { TbSelector } from 'react-icons/tb';

import { CardData } from '../types/CardData';
import { GameFormat } from '../types/GameFormat';
import { Hole } from '../types/Hole';
import { Team } from '../types/Team';
import { LinkedEvent, Tournament } from '../types/Tournament';
import {
  calculateSingleRoundLeaderboard,
  getLinkedEventAndCards,
  getTeamFirstAndLastNames,
  isBestBallTypeFormat,
  isStablefordFormat,
} from '../utils/utils';
import FormField from './FormField';
import LeaderboardDetails from './LeaderboardDetails';
import MultiplayerLeaderboardDetails from './MultiplayerLeaderboardDetails';

export default function Leaderboard({
  teams,
  event,
  isNet,
  setIsNet,
  selectedFlight,
  setSelectedFlight,
  selectedPod,
  setSelectedPod,
  isPayouts = false,
  isResultsReport = false,
  hideFlightTags = false,
}: {
  teams: CardData[];
  event: Tournament;
  isNet: boolean;
  setIsNet: (val: boolean) => void;
  selectedFlight: string;
  setSelectedFlight: (val: string) => void;
  selectedPod: string;
  setSelectedPod: (val: string) => void;
  isPayouts?: boolean;
  isResultsReport?: boolean;
  hideFlightTags?: boolean;
}) {
  const [eventTeams, setEventTeams] = useState<Team[]>(event.teams as Team[]);
  const [showEventSelectorToggle, setShowEventSelectorToggle] =
    useState<boolean>(false);

  const [selectedLinkedEvent, setSelectedLinkedEvent] =
    useState<LinkedEvent | null>(null);

  const [accordionOpenState, setAccordionOpenState] = useState<boolean[]>([]);

  const USDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const USDollarWhole = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const hasPayoutPoints = event.teams?.some(
    (val: Team) =>
      (val.points != null && val.points > 0) ||
      (val.netPoints != null && val.netPoints > 0)
  );

  document.addEventListener('click', (e) => {
    if (e.shiftKey && e.metaKey) {
      setAccordionOpenState(new Array(teams.length).fill(true));
    }
  });

  let groupedTeamsReturned: CardData[][] = [];
  let teamsWithDetails: CardData[] = [];

  let flights = ['All', ...(event.flights ?? [])];
  let pods = ['All'];
  eventTeams.forEach((team: Team) => {
    if (
      team.flight === selectedFlight &&
      team.pod != null &&
      !pods.includes(team.pod)
    ) {
      pods.push(team.pod);
    }
  });

  if (selectedLinkedEvent != null && event.linkedEvents != null) {
    let upToDateSelectedLinkedEvent = event.linkedEvents.find(
      (val: LinkedEvent) => val.name === selectedLinkedEvent.name
    );

    if (upToDateSelectedLinkedEvent == null) {
      upToDateSelectedLinkedEvent = selectedLinkedEvent as LinkedEvent;
    }
    flights = ['All', ...(upToDateSelectedLinkedEvent?.flights ?? [])];
    pods = ['All'];
    upToDateSelectedLinkedEvent.teams.forEach((team: Team) => {
      if (
        team.flight === selectedFlight &&
        team.pod != null &&
        !pods.includes(team.pod)
      ) {
        pods.push(team.pod);
      }
    });
    // if selected flight is not in flights, set it to 'All'
    if (
      !flights.includes(selectedFlight) &&
      event.multiRoundEventCode == null
    ) {
      setSelectedFlight('All');
    }

    // if selected pod is not in pods, set it to 'All'
    if (!pods.includes(selectedPod) && event.multiRoundEventCode == null) {
      setSelectedPod('All');
    }

    const { linkedEventAsTournament, linkedEventCards } =
      getLinkedEventAndCards(event, upToDateSelectedLinkedEvent, teams);

    const { groupedTeams, flightTeams } = calculateSingleRoundLeaderboard(
      linkedEventAsTournament,
      linkedEventCards as CardData[],
      isNet,
      selectedFlight,
      selectedPod,
      isPayouts
    );

    teamsWithDetails = flightTeams;
    groupedTeamsReturned = groupedTeams;
  } else {
    flights = ['All', ...(event?.flights ?? [])];
    pods = ['All'];
    eventTeams.forEach((team: Team) => {
      if (
        team.flight === selectedFlight &&
        team.pod != null &&
        !pods.includes(team.pod)
      ) {
        pods.push(team.pod);
      }
    });
    // if selected flight is not in flights, set it to 'All'
    if (
      !flights.includes(selectedFlight) &&
      event.multiRoundEventCode == null
    ) {
      setSelectedFlight('All');
    }

    // if selected pod is not in pods, set it to 'All'
    if (!pods.includes(selectedPod) && event.multiRoundEventCode == null) {
      setSelectedPod('All');
    }

    const { groupedTeams, flightTeams } = calculateSingleRoundLeaderboard(
      event,
      teams,
      isNet,
      selectedFlight,
      selectedPod,
      isPayouts
    );
    teamsWithDetails = flightTeams;
    groupedTeamsReturned = groupedTeams;
  }

  if (event.hideLeaderboard && !isResultsReport) {
    return (
      <div className={`flex flex-col items-center text-center mt-6 mx-2`}>
        <div className="text-xl font-bold">
          <h1>The leaderboard has been hidden for this event.</h1>

          <div className="text-md font-normal mb-4 mt-2">
            <h1>
              Check back in later to see the results. In the meantime, go make
              some birdies!
            </h1>
          </div>
        </div>
      </div>
    );
  }

  function getTextSize() {
    return 'text-sm';
  }

  function handleRowClick(index: number) {
    if (event.hideLeaderboardScorecards || isResultsReport) {
      return;
    }
    const rows = accordionOpenState;
    const teamNumber = teamsWithDetails.filter(
      (val: CardData) =>
        (val.flight === selectedFlight || selectedFlight === 'All') &&
        (val.pod === selectedPod || selectedPod === 'All')
    )[index]?.teamNumber;
    if (teamNumber !== undefined) {
      rows[teamNumber] = !rows[teamNumber];
    }
    setAccordionOpenState([...rows]);
  }

  function formatPayouts(amount: number) {
    if (amount % 1 === 0) {
      return USDollarWhole.format(amount);
    }
    return USDollar.format(amount);
  }

  const showNetPayouts =
    (isNet && event.netPayoutsSet && selectedLinkedEvent === null) ||
    (selectedLinkedEvent != null && isNet && selectedLinkedEvent.netPayoutsSet);

  const showGrossPayouts =
    (!isNet && event.payoutsSet && selectedLinkedEvent === null) ||
    (selectedLinkedEvent != null && !isNet && selectedLinkedEvent.payoutsSet);

  return (
    <div>
      <div>
        {event.sponsorImageURL !== undefined &&
          event.sponsorImageURL.length > 0 && (
            <img
              alt="logo"
              className="max-h-96 max-w-full mx-auto"
              src={event.sponsorImageURL}
            />
          )}
        {!isPayouts && !isResultsReport && (
          <div className="flex flex-col text-center">
            <div className="md:text-3xl text-xl font-bold">
              <h1
                className={`flex items-center justify-center ${
                  event.linkedEvents != null &&
                  event.linkedEvents.length > 0 &&
                  'hover:cursor-pointer'
                }`}
                onClick={() => {
                  if (
                    event.linkedEvents != null &&
                    event.linkedEvents.length > 0
                  ) {
                    setShowEventSelectorToggle(!showEventSelectorToggle);
                  }
                }}
              >
                {selectedLinkedEvent == null
                  ? event.name
                  : selectedLinkedEvent.name}{' '}
                {event.linkedEvents != null &&
                  event.linkedEvents.length > 0 && <TbSelector />}
              </h1>
            </div>
            <div className="text-md font-normal mb-4">
              <h1>{event.date}</h1>
            </div>
          </div>
        )}
        {!isResultsReport && (
          <div
            className={`flex ${
              flights.length > 1 ? 'justify-between' : 'justify-center'
            } md:justify-center md:flex-col items-end md:items-center mb-3 mx-2`}
          >
            <div>
              {flights.length > 1 ? (
                <div
                  className={`flex w-48 ${
                    flights.length > 1 ? 'justify-between' : 'justify-center'
                  } md:justify-center items-center justify-center mt-2 ${getTextSize()}`}
                >
                  <label>
                    {hideFlightTags && 'Flight'}
                    <select
                      className="border rounded p-2 flex items-center md:mb-3 !w-48"
                      onChange={(e) => {
                        if (e.target.value === 'All') {
                          setSelectedPod('All');
                        }
                        setSelectedFlight(e.target.value);
                      }}
                      value={selectedFlight}
                      title="Flight"
                    >
                      {flights.map((flight: string, i: number) => {
                        return (
                          <option value={flight} key={i}>
                            {flight}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                </div>
              ) : null}

              {pods.length > 1 && selectedFlight !== 'All' ? (
                <div
                  className={`flex w-48 ${
                    pods.length > 1 ? 'justify-between' : 'justify-center'
                  } md:justify-center items-center justify-center ${getTextSize()}`}
                >
                  <label>
                    Pod
                    <select
                      className="border rounded p-2 flex items-center mb-1 !w-48"
                      onChange={(e) => setSelectedPod(e.target.value)}
                      value={selectedPod}
                    >
                      {pods.map((pod: string, i: number) => {
                        return (
                          <option value={pod} key={i}>
                            {pod}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                </div>
              ) : null}
            </div>
            {event.isGrossOnly !== true &&
              event.isNetOnly !== true &&
              event.startAtHandicap !== true && (
                <div className={`${getTextSize()}`}>
                  <span className="mr-4">Gross</span>
                  <Switch
                    checked={isNet}
                    onChange={setIsNet}
                    className={`${
                      isNet ? 'bg-primary' : 'bg-tertiary'
                    } relative inline-flex h-6 w-11 items-center rounded-full`}
                    data-test="leaderboard-net-toggle"
                  >
                    <span
                      className={`transform transition ease-in-out duration-200 ${
                        isNet ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white`}
                    />
                  </Switch>
                  <span className="ml-4">Net</span>
                </div>
              )}
          </div>
        )}

        <table className="w-full lg:w-4/5 mx-auto shadow-lg">
          <thead className="bg-gray-50 border-b-2 border-gray-200">
            <tr>
              <th
                className={`${
                  isResultsReport ? 'p-1' : 'p-3'
                } ${getTextSize()} font-semibold tracking-wide text-left`}
              >
                Pos.
              </th>
              <th
                className={`${
                  isResultsReport ? 'p-1' : 'p-3'
                } ${getTextSize()} font-semibold tracking-wide text-left`}
              >
                {event.playersPerTeam !== undefined && event.playersPerTeam > 1
                  ? 'Players'
                  : 'Player'}
              </th>
              <th
                className={`${
                  isResultsReport ? 'p-1' : 'p-3'
                } ${getTextSize()} font-semibold tracking-wide text-left ${
                  event.isGrossOnly || event.startAtHandicap
                    ? 'hidden'
                    : !isNet
                    ? 'hidden md:block'
                    : ''
                }`}
              >
                Net
              </th>
              <th
                className={`${
                  isResultsReport ? 'p-1' : 'p-3'
                } ${getTextSize()} font-semibold tracking-wide text-left ${
                  event.isNetOnly ? 'hidden' : isNet ? 'hidden md:block' : ''
                }`}
              >
                {event.startAtHandicap && isStablefordFormat(event.format)
                  ? 'Points'
                  : 'Score'}
              </th>
              {isPayouts ? (
                <>
                  <th
                    className={`${getTextSize()} font-semibold tracking-wide text-center`}
                  >
                    Gross Payout
                  </th>
                  <th
                    className={`${getTextSize()} font-semibold tracking-wide text-center`}
                  >
                    Net Payout
                  </th>
                  <th
                    className={`p-1 ${getTextSize()} font-semibold tracking-wide text-center`}
                  >
                    Gross Points
                  </th>
                  <th
                    className={`p-1 ${getTextSize()} font-semibold tracking-wide text-center`}
                  >
                    Net Points
                  </th>
                </>
              ) : (
                <>
                  <th
                    className={`hidden md:table-cell ${getTextSize()} font-semibold tracking-wide text-center p-2`}
                  >
                    {showNetPayouts || showGrossPayouts ? 'Payouts' : 'Thru'}
                  </th>
                  <th
                    className={`table-cell md:hidden ${
                      showNetPayouts || showGrossPayouts
                        ? 'text-lg'
                        : getTextSize()
                    } font-semibold tracking-wide text-center p-2`}
                  >
                    {showNetPayouts || showGrossPayouts ? '💰' : 'Thru'}
                  </th>
                  {hasPayoutPoints && (
                    <>
                      <th
                        className={`hidden md:table-cell ${getTextSize()} font-semibold tracking-wide text-center p-2`}
                      >
                        Points
                      </th>
                      <th
                        className={`text-secondary table-cell md:hidden text-lg font-semibold tracking-wide text-center p-2`}
                      >
                        ⭐
                      </th>
                    </>
                  )}
                  {!isResultsReport && (
                    <th
                      className={`p-1 ${getTextSize()} font-semibold tracking-wide text-left`}
                    ></th>
                  )}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {teamsWithDetails.length === 0
              ? Array.from({ length: 10 }, (_, i) => i + 1).map((row) => (
                  <tr key={row} className="animate-pulse w-full">
                    <td
                      className="px-6 py-4 border-b border-gray-200 w-full"
                      colSpan={20}
                    >
                      <div className="h-6 bg-gray-200 rounded w-full"></div>
                    </td>
                  </tr>
                ))
              : teamsWithDetails
                  .filter(
                    (val: CardData) =>
                      (val.flight === selectedFlight ||
                        selectedFlight === 'All') &&
                      (val.pod === selectedPod || selectedPod === 'All')
                  )
                  .map((team: CardData, i: number) => {
                    return (
                      <React.Fragment key={i}>
                        <tr
                          onClick={() => handleRowClick(i)}
                          key={i}
                          className={`hover:cursor-pointer ${
                            i % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }`}
                          data-test={`leaderboard-row-${i}`}
                        >
                          <td
                            className={`${
                              isResultsReport ? 'p-1' : 'p-3'
                            } ${getTextSize()} text-gray-700`}
                            data-test={`leaderboard-row-${i}-place`}
                          >
                            {team.place}
                          </td>
                          <td
                            className={`${
                              isResultsReport ? 'p-1' : 'p-3'
                            } ${getTextSize()} text-gray-700`}
                            data-test={`leaderboard-row-${i}-name`}
                          >
                            {event.showFirstAndLastNames &&
                            !team.hasCustomTeamName
                              ? getTeamFirstAndLastNames(team)
                              : team.name}
                            {event.takeItScoring && !selectedLinkedEvent && (
                              <div className="text-xs tracking-tighter font-bold">
                                {/* Get how many holes the user has taken so far */}
                                {
                                  team.scores.filter((s: Hole) => s.taken)
                                    .length
                                }{' '}
                                {team.scores.filter((s: Hole) => s.taken)
                                  .length !== 1
                                  ? 'HOLES'
                                  : 'HOLE'}{' '}
                                TAKEN
                              </div>
                            )}
                          </td>
                          {isStablefordFormat(
                            (selectedLinkedEvent != null &&
                            event.linkedEvents != null
                              ? selectedLinkedEvent.format
                              : event.format) as GameFormat
                          ) ? (
                            <>
                              <td
                                className={`${
                                  isResultsReport ? 'p-1' : 'p-3'
                                } ${getTextSize()} ${
                                  event.isGrossOnly || event.startAtHandicap
                                    ? 'hidden'
                                    : !isNet
                                    ? 'hidden md:table-cell'
                                    : ''
                                }`}
                                data-test={`leaderboard-row-${i}-net`}
                              >
                                {team.totals.netPointsTotal}
                              </td>
                              <td
                                className={`${
                                  isResultsReport ? 'p-1' : 'p-3'
                                } ${getTextSize()} ${
                                  event.isNetOnly
                                    ? 'hidden'
                                    : isNet
                                    ? 'hidden md:table-cell'
                                    : ''
                                }`}
                                data-test={`leaderboard-row-${i}-gross`}
                              >
                                {team.totals.grossPointsTotal}
                              </td>
                            </>
                          ) : (
                            <>
                              <td
                                className={`${
                                  isResultsReport ? 'p-1' : 'p-3'
                                } ${getTextSize()} ${
                                  team.totals.netToPar < 0
                                    ? 'text-red-500'
                                    : team.totals.netToPar === 0
                                    ? 'text-green-500'
                                    : 'text-gray-900'
                                } ${
                                  event.isGrossOnly || event.startAtHandicap
                                    ? 'hidden'
                                    : !isNet
                                    ? 'hidden md:table-cell'
                                    : ''
                                }`}
                                data-test={`leaderboard-row-${i}-net`}
                              >
                                {event.showTotalScore
                                  ? team.totals.netTotal
                                  : team.totals.netToParFormatted}
                              </td>
                              <td
                                className={`${
                                  isResultsReport ? 'p-1' : 'p-3'
                                } ${getTextSize()} ${
                                  team.totals.grossToPar < 0
                                    ? 'text-red-500'
                                    : team.totals.grossToPar === 0
                                    ? 'text-green-500'
                                    : 'text-gray-900'
                                } ${
                                  event.isNetOnly
                                    ? 'hidden'
                                    : isNet
                                    ? 'hidden md:table-cell'
                                    : ''
                                }`}
                                data-test={`leaderboard-row-${i}-gross`}
                              >
                                {event.showTotalScore
                                  ? team.totals.grossTotal
                                  : team.totals.grossToParFormatted}
                              </td>
                            </>
                          )}

                          {isPayouts ? (
                            <>
                              <td
                                className={`${
                                  isResultsReport ? 'p-1' : 'p-3'
                                } ${getTextSize()} tracking-wide text-center hover:cursor-pointer`}
                                onClick={(e: any) => e.stopPropagation()}
                              >
                                <FormField
                                  type="number"
                                  value={
                                    eventTeams.find(
                                      (val: Team) =>
                                        val.teamNumber === team.teamNumber
                                    )?.payout ?? ''
                                  }
                                  title=""
                                  onChange={(val: string) => {
                                    const newPayout =
                                      val.length > 0 ? parseFloat(val) : null;
                                    const updatedTeams = [...eventTeams];
                                    (
                                      updatedTeams.find(
                                        (t: Team) =>
                                          t.teamNumber === team.teamNumber
                                      ) as Team
                                    ).payout = newPayout;
                                    setEventTeams(updatedTeams);
                                  }}
                                  width={'24'}
                                />
                              </td>
                              <td
                                className={`${
                                  isResultsReport ? 'p-1' : 'p-3'
                                } ${getTextSize()} tracking-wide text-center hover:cursor-pointer`}
                                onClick={(e: any) => e.stopPropagation()}
                              >
                                <FormField
                                  type="number"
                                  value={
                                    eventTeams.find(
                                      (val: Team) =>
                                        val.teamNumber === team.teamNumber
                                    )?.netPayout ?? ''
                                  }
                                  title=""
                                  onChange={(val: string) => {
                                    const newPayout =
                                      val.length > 0 ? parseFloat(val) : null;
                                    const updatedTeams = [...eventTeams];
                                    (
                                      updatedTeams.find(
                                        (t: Team) =>
                                          t.teamNumber === team.teamNumber
                                      ) as Team
                                    ).netPayout = newPayout;
                                    setEventTeams(updatedTeams);
                                  }}
                                  width={'24'}
                                />
                              </td>
                              <td
                                className={`${
                                  isResultsReport ? 'p-1' : 'p-3'
                                } ${getTextSize()} tracking-wide text-center hover:cursor-pointer`}
                                onClick={(e: any) => e.stopPropagation()}
                              >
                                <FormField
                                  type="number"
                                  value={
                                    eventTeams.find(
                                      (val: Team) =>
                                        val.teamNumber === team.teamNumber
                                    )?.points ?? ''
                                  }
                                  title=""
                                  onChange={(val: string) => {
                                    const newPoints =
                                      val.length > 0 ? parseFloat(val) : null;
                                    const updatedTeams = [...eventTeams];
                                    (
                                      updatedTeams.find(
                                        (t: Team) =>
                                          t.teamNumber === team.teamNumber
                                      ) as Team
                                    ).points = newPoints;
                                    setEventTeams(updatedTeams);
                                  }}
                                  width={'24'}
                                />
                              </td>
                              <td
                                className={`${
                                  isResultsReport ? 'p-1' : 'p-3'
                                } ${getTextSize()} tracking-wide text-center hover:cursor-pointer`}
                                onClick={(e: any) => e.stopPropagation()}
                              >
                                <FormField
                                  type="number"
                                  value={
                                    eventTeams.find(
                                      (val: Team) =>
                                        val.teamNumber === team.teamNumber
                                    )?.netPoints ?? ''
                                  }
                                  title=""
                                  onChange={(val: string) => {
                                    const newPayout =
                                      val.length > 0 ? parseFloat(val) : null;
                                    const updatedTeams = [...eventTeams];
                                    (
                                      updatedTeams.find(
                                        (t: Team) =>
                                          t.teamNumber === team.teamNumber
                                      ) as Team
                                    ).netPoints = newPayout;
                                    setEventTeams(updatedTeams);
                                  }}
                                  width={'24'}
                                />
                              </td>
                            </>
                          ) : (
                            <>
                              {isNet ? (
                                <td
                                  className={`p-1 text-center ${getTextSize()} text-gray-700`}
                                  data-test={`leaderboard-row-${i}-thru-payout`}
                                >
                                  {showNetPayouts
                                    ? !isNullOrUndefined(
                                        (
                                          selectedLinkedEvent ?? event
                                        ).teams?.find(
                                          (val: Team) =>
                                            val.teamNumber === team.teamNumber
                                        )?.netPayout
                                      )
                                      ? `${formatPayouts(
                                          (
                                            selectedLinkedEvent ?? event
                                          ).teams?.find(
                                            (val: Team) =>
                                              val.teamNumber === team.teamNumber
                                          )?.netPayout ?? 0
                                        )}`
                                      : '-'
                                    : team.totals.thru !== team.scores.length
                                    ? team.totals.thru
                                    : 'F'}
                                </td>
                              ) : (
                                <td
                                  className={`p-1 text-center ${getTextSize()} text-gray-700`}
                                  data-test={`leaderboard-row-${i}-thru-payout`}
                                >
                                  {showGrossPayouts
                                    ? !isNullOrUndefined(
                                        (
                                          selectedLinkedEvent ?? event
                                        ).teams?.find(
                                          (val: Team) =>
                                            val.teamNumber === team.teamNumber
                                        )?.payout
                                      )
                                      ? `${formatPayouts(
                                          (
                                            selectedLinkedEvent ?? event
                                          ).teams?.find(
                                            (val: Team) =>
                                              val.teamNumber === team.teamNumber
                                          )?.payout ?? 0
                                        )}`
                                      : '-'
                                    : team.totals.thru !== team.scores.length
                                    ? team.totals.thru
                                    : 'F'}
                                </td>
                              )}
                              {hasPayoutPoints && !isNet && (
                                <td
                                  className={`p-1 text-center ${getTextSize()} text-gray-700`}
                                  data-test={`leaderboard-row-${i}-points`}
                                >
                                  {(selectedLinkedEvent ?? event).teams?.find(
                                    (val: Team) =>
                                      val.teamNumber === team.teamNumber
                                  )?.points ?? '-'}
                                </td>
                              )}

                              {hasPayoutPoints && isNet && (
                                <td
                                  className={`p-1 text-center ${getTextSize()} text-gray-700`}
                                  data-test={`leaderboard-row-${i}-points`}
                                >
                                  {(selectedLinkedEvent ?? event).teams?.find(
                                    (val: Team) =>
                                      val.teamNumber === team.teamNumber
                                  )?.netPoints ?? '-'}
                                </td>
                              )}
                              {!isResultsReport && (
                                <td
                                  className={`p-2 ${getTextSize()} text-gray-700`}
                                >
                                  {event.hideLeaderboardScorecards ? null : accordionOpenState[
                                      team.teamNumber ?? -1
                                    ] ? (
                                    <MdKeyboardArrowUp
                                      className={`font-thin ${getTextSize()}`}
                                    />
                                  ) : (
                                    <MdKeyboardArrowDown
                                      className={`font-thin ${getTextSize()}`}
                                    />
                                  )}
                                </td>
                              )}
                            </>
                          )}
                        </tr>
                        <tr
                          key={team.name + team.ghin + team.handicap}
                          className={
                            accordionOpenState[team.teamNumber ?? -1]
                              ? 'justify-center items-center'
                              : 'hidden'
                          }
                        >
                          <td
                            colSpan={10}
                            className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                          >
                            {team.teamNumber !== undefined &&
                            isBestBallTypeFormat(
                              selectedLinkedEvent != null &&
                                event.linkedEvents != null
                                ? selectedLinkedEvent.format
                                : event.format
                            ) ? (
                              <MultiplayerLeaderboardDetails
                                event={event}
                                players={
                                  groupedTeamsReturned.find(
                                    (val: CardData[]) =>
                                      val[0]?.teamNumber === team.teamNumber
                                  ) as CardData[]
                                }
                                isNet={isNet}
                                team={team}
                                rounds={[event]}
                                showTotals={true}
                                format={
                                  (selectedLinkedEvent != null &&
                                  event.linkedEvents != null
                                    ? selectedLinkedEvent.format
                                    : event.format) as GameFormat
                                }
                              />
                            ) : (
                              <LeaderboardDetails
                                event={event}
                                team={team}
                                onHoleClick={() => null}
                                isGrossOnly={event.isGrossOnly === true}
                                startAtHandicap={event.startAtHandicap === true}
                              />
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
          </tbody>
        </table>
      </div>
      {showEventSelectorToggle && (
        <div
          className="w-screen h-screen z-10 backdrop-filter backdrop-blur absolute top-0 left-0"
          onClick={() => setShowEventSelectorToggle(false)}
        >
          <div className="w-96 h-auto mx-auto">
            <div className="hover:cursor-pointer mx-auto flex flex-col items-center font-bold text-2xl">
              <div
                onClick={() => {
                  setSelectedLinkedEvent(null);
                  setShowEventSelectorToggle(false);
                }}
                className="hover:bg-gray-100 p-2 w-96 text-center"
              >
                {event.name}
              </div>
              {event.linkedEvents?.map((val: LinkedEvent, i: number) => {
                return (
                  <div
                    className="hover:bg-gray-100 p-2 w-96 text-center"
                    key={i}
                    onClick={() => {
                      const newEvent = event.linkedEvents?.find(
                        (le: LinkedEvent) => val.name === le.name
                      ) as LinkedEvent;
                      if (newEvent == null) {
                        setSelectedLinkedEvent(null);
                      } else {
                        setSelectedLinkedEvent(newEvent);
                      }
                      setShowEventSelectorToggle(false);
                    }}
                  >
                    {val.name}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
